import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { isMobileBreakpoint } from '@theme/utilities';

export class QuickViewComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productLink = productCard?.getProductCardLink();
    if (!productLink?.href) return '';

    const url = new URL(productLink.href);
    const selectedVariantId = productCard?.getSelectedVariantId?.();
    if (selectedVariantId && !url.searchParams.has('variant')) {
      url.searchParams.set('variant', selectedVariantId);
    }
    return url.toString();
  }

  /**
   * Handles the quick view button click. Fetches the product page, extracts
   * the product grid content, and morphs it into the shared quick-add modal.
   * @param {Event} event
   */
  handleClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const url = this.productPageUrl;
    if (!url) return;

    const html = await this.#fetchProductPage(url);
    const grid = html?.querySelector('[data-product-grid-content]');
    if (!grid) return;

    this.#updateModal(/** @type {Element} */ (grid.cloneNode(true)));
    this.#injectDescription();
    this.#removeInventoryStatus();
    this.#resetQuantitySelectors();
    this.#openDialog();
  };

  #removeInventoryStatus() {
    const modalContent = document.getElementById('quick-add-modal-content');
    modalContent?.querySelectorAll('product-inventory').forEach((n) => n.remove());
  }

  /**
   * The quantity selector component latches `serverDisabledMinus = true` on
   * its first connectedCallback if the minus button arrived disabled (which
   * it does when value === min on initial render). After that, the component
   * refuses to re-enable the button when the user clicks plus, so the minus
   * appears permanently broken inside the modal. Clear the latch and refresh
   * the button states so the normal value-vs-min logic takes over.
   */
  #resetQuantitySelectors() {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    queueMicrotask(() => {
      const selectors = modalContent.querySelectorAll('quantity-selector-component');
      for (const selector of selectors) {
        const sel = /** @type {any} */ (selector);
        sel.serverDisabledMinus = false;
        sel.serverDisabledPlus = false;
        if (typeof sel.updateButtonStates === 'function') sel.updateButtonStates();
      }
    });
  }

  #injectDescription() {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    modalContent.querySelectorAll('.quick-view__description-block, .quick-view__description').forEach((n) => n.remove());

    const description = this.dataset.productDescription?.trim();
    if (!description) return;

    // Mobile flattened layout: insert plain div right after .product-header.
    const productHeader = modalContent.querySelector('.product-header');
    if (productHeader) {
      const div = document.createElement('div');
      div.className = 'quick-view__description';
      div.textContent = description;
      productHeader.parentElement?.insertBefore(div, productHeader.nextSibling);
      return;
    }

    // Desktop layout: wrap in .group-block / .group-block-content so it
    // passes the modal's allowlist + matches the column flow.
    const anchor = modalContent.querySelector('product-price');
    if (!anchor) return;

    const blockWrapper = anchor.closest('.group-block') || anchor;

    const wrapper = document.createElement('div');
    wrapper.className = 'group-block group-block--height-fit group-block--width-fill quick-view__description-block';
    wrapper.setAttribute('data-testid', 'group-block');

    const content = document.createElement('div');
    content.className = 'group-block-content';

    const div = document.createElement('div');
    div.className = 'quick-view__description';
    div.textContent = description;

    content.appendChild(div);
    wrapper.appendChild(content);
    blockWrapper.parentElement?.insertBefore(wrapper, blockWrapper.nextSibling);
  }

  /**
   * @param {string} productPageUrl
   * @returns {Promise<Document | null>}
   */
  async #fetchProductPage(productPageUrl) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, { signal: this.#abortController.signal });
      if (!response.ok) return null;
      const text = await response.text();
      return new DOMParser().parseFromString(text, 'text/html');
    } catch (error) {
      if (error.name === 'AbortError') return null;
      throw error;
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * @param {Element} productGrid
   */
  #updateModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    if (isMobileBreakpoint()) {
      this.#flattenForMobile(productGrid);
    }

    morph(modalContent, productGrid);
  }

  /**
   * On mobile, the desktop side-by-side .product-details layout doesn't fit.
   * Mirror the quick-add modal's mobile restructuring: pull out title + price
   * into a single .product-header row above the rest of the form, and append
   * the variant picker + form so they stack full-width.
   * @param {Element} productGrid
   */
  #flattenForMobile(productGrid) {
    const productDetails = productGrid.querySelector('.product-details');
    const productFormComponent = productGrid.querySelector('product-form-component');
    const variantPicker = productGrid.querySelector('variant-picker');
    const productPrice = productGrid.querySelector('product-price');

    const productTitle = document.createElement('a');
    productTitle.textContent = this.dataset.productTitle || '';
    productTitle.href = this.productPageUrl;

    const productHeader = document.createElement('div');
    productHeader.classList.add('product-header');
    productHeader.appendChild(productTitle);
    if (productPrice) productHeader.appendChild(productPrice);
    productGrid.appendChild(productHeader);

    if (variantPicker) productGrid.appendChild(variantPicker);
    if (productFormComponent) productGrid.appendChild(productFormComponent);

    productDetails?.remove();
  }

  #openDialog() {
    const dialogComponent = /** @type {HTMLElement & { showDialog?: () => void } | null} */ (
      document.getElementById('quick-add-dialog')
    );
    if (dialogComponent && typeof dialogComponent.showDialog === 'function') {
      dialogComponent.showDialog();
    }
  }
}

if (!customElements.get('quick-view-component')) {
  customElements.define('quick-view-component', QuickViewComponent);
}
